import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { BaseQuestionComponent } from '../../question.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { JuiIcon } from '@jump-tech-frontend/core-components-angular';
import { QuestionHintComponent } from '@jump-tech-frontend/question-components';
import { FileUploadQuestionInputComponent } from './file-upload-question-input.component';
import { QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';


@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'crds-question-file-upload',
    template: `
    @if (show && form) {
      <div [formGroup]="form">
        @if (question.label) {
          <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
            <question-label-content
              [question]="question"
              [hideHint]="hasUploadedFiles"
              [isInvalid]="isInvalid"
              [i18ns]="i18ns"
            ></question-label-content>
          </label>
        }
        <div class="inline-buttons">
          <crds-question-file-upload-input
            (fileUploaded)="onFileUploaded($event)"
            [hasUploadedFiles]="hasUploadedFiles"
            [form]="form"
            [question]="question"
            [i18ns]="i18ns"
          ></crds-question-file-upload-input>
        </div>
        <div class="file-wrapper">
          @if (!hasUploadedFiles) {
            <question-hint [question]="question"></question-hint>
          }
          @if (hasUploadedFiles) {
            <div>
              @for (uploadedFile of files; track uploadedFile; let i = $index) {
                <div class="file-placeholder-container pt-1">
                  <div class="file-placeholder-icon">
                    <i class="material-icons">article</i>
                  </div>
                  <div class="file-placeholder">
                    <span [id]="i + '_temp'">{{ uploadedFile.name }}</span>
                  </div>
                  <jui-icon (click)="clearFile(i)" isButton="true" name="delete_forever" #fileUploadButton></jui-icon>
                </div>
              }
            </div>
          }
        </div>
        <ngx-spinner bdColor="rgba(51, 51, 51, 0.8)" size="large" type="line-scale"></ngx-spinner>
      </div>
    }
    `,
    styles: [
        `
      .material-icons {
        color: #ffffff;
      }
      .file-placeholder-container {
        padding-right: 1em;
        margin-bottom: 0.5em;
        display: grid;
        grid-template-columns: 1fr 8fr 1fr;
        align-items: center;
        border-bottom-style: solid;
        border-width: thin;
      }
      .file-placeholder-container.file-upload-button {
      }

      .file-placeholder-icon {
        padding: 0;
      }

      .file-placeholder-icon i {
        font-size: 3rem;
        color: var(--primary-color) !important;
      }

      .file-placeholder {
      }

      .file-upload-button {
        font-size: 0.8rem;
        display: inline-flex;
        margin-bottom: 0.75rem;
        width: max-content;
      }

      .inline-buttons {
        display: grid;
        grid-template-columns: 50% 50%;
      }
    `
    ],
    imports: [
    ReactiveFormsModule,
    QuestionLabelContentComponent,
    FileUploadQuestionInputComponent,
    QuestionHintComponent,
    JuiIcon,
    NgxSpinnerModule
]
})
export class FileUploadQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<any>;
  @Input() i18ns: I18nKeys;

  files: any[];

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    /**
     * Check for a non-array value
     */
    if (this.value && !(this.value instanceof Array)) {
      setTimeout(() => {
        this.form?.get(this.question.key)?.patchValue([this.value]);
      });
    }
    this.setUploadedFiles();
  }

  get isInvalid() {
    return this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  get hasUploadedFiles() {
    return this.files.length > 0;
  }

  clearFile(index: number) {
    this.setUploadedFiles();
    this.files.splice(index, 1);
    this.form?.get(this.question.key)?.patchValue(this.files);
  }

  onFileUploaded($event: { name: string; data: string }) {
    this.setUploadedFiles();
    const { name } = $event;
    const index = this.files.indexOf(x => x.name === name);
    if (index > -1) {
      this.files.splice(index, 1);
    }

    if (this.question.sort && this.question.sort === 'desc') {
      this.files.unshift($event);
    } else {
      this.files.push($event);
    }
    this.form?.get(this.question.key)?.patchValue(this.files);
  }

  setUploadedFiles() {
    const values =
      this.form.get(this.question.key) && this.form?.get(this.question.key)?.value
        ? this.form?.get(this.question.key)?.value
        : [];
    this.files = [...values];
  }
}
